import { createBasicColorSet, cssColorValue, KeyboardDetectionProvider, LoadingIndicator, } from '@spotify-internal/encore-web';
import React from 'react';
import styled, { css } from 'styled-components';
import { CenteredPlayButton, FullControls } from './VideoControls';
import { VideoOverlay } from './VideoOverlay';
import '@mux/mux-video';
import { getIsControlButtonTarget, getPoster } from './utils';
import { useVideoPlayer } from './useVideoPlayer';
const LoadingOverlay = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  height: 100%;
  width: 100%;
`;
const VideoPlayerContainer = styled.div `
  display: flex;
  background-size: ${({ backgroundImage }) => backgroundImage ? 'contain' : 'unset'};
  background-image: ${({ backgroundImage }) => backgroundImage ? `url(${backgroundImage})` : 'none'};
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  border: none;
  padding: 0;
  cursor: pointer;
  :focus-visible {
    outline: 3px solid ${cssColorValue('textBase')};
    outline-offset: 2px;
  }
  flex-direction: column;
  position: relative;
  width: 100%;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio};
  overflow: hidden;
  .buttons {
    z-index: 200;
    overflow: hidden;
    padding-top: 8px;
    padding-bottom: 8px;
    top: ${({ aspectRatio }) => (aspectRatio === '9 / 16' ? '0' : 'unset')};
    bottom: ${({ aspectRatio }) => (aspectRatio === '9 / 16' ? 'unset' : '0')};
    opacity: 0;
    transition: all 0.3s cubic-bezier(0.54, 0, 0.5, 1);
  }
  ${({ showFullControls }) => showFullControls &&
    css `
      .buttons {
        opacity: 1;
        padding-top: 12px;
        padding-bottom: 12px;
      }
    `}
  .loading-overlay {
    opacity: 0%;
  }
  ${({ loading, showDisabledOverlay }) => loading &&
    !showDisabledOverlay &&
    css `
      .loading-overlay {
        opacity: 60%;
      }
    `}
  :after {
    content: ' ';
    position: absolute;
    background: black;
    opacity: 0%;
    height: 100%;
    width: 100%;
    transform: translateZ(100px);
    transition: all 0.5s cubic-bezier(0.75, 0, 0, 1);
  }
  ${({ showDisabledOverlay }) => showDisabledOverlay &&
    css `
      :after {
        opacity: 60%;
      }
    `}
  .overlay {
    opacity: 0%;
    transition: all 0.5s cubic-bezier(0.75, 0, 0, 1);
  }
  ${({ showBlurbAndMetadata }) => showBlurbAndMetadata &&
    css `
      .overlay {
        opacity: 100%;
      }
    `}

  > mux-video {
    width: 100%;
    display: flex;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
  }
`;
const ControlsAndOverlay = styled.div `
  display: flex;
  flex-direction: column;
  text-align: start;
  position: relative;
  height: 100%;
  width: 100%;
`;
export const Player = ({ video, useDefaultThumbnail, customThumbnailType, customThumbnailTimestamp, customThumbnailImage, aspectRatio, includeSound, autoplay = false, playOnHover = false, playButtonOnLoad, videoControlColors, blurb, metadata, disabled = false, disabledUntilHover = false, toggleVolumeOnClick = false, videoIndex, preload = 'none', }) => {
    const { ref, showFullControls, showDisabledOverlay, showPlayButtonOnly, onMouseEnter, onMouseLeave, onFocus, onBlur, togglePlayButton, toggleVolume, onClick, onKeyboardEnter, startTime, isPlaying, isMuted, isLoading, } = useVideoPlayer({
        playOnHover,
        disabled,
        disabledUntilHover,
        playButtonOnLoad,
        autoplay,
        toggleVolumeOnClick,
        includeSound,
        videoIndex,
        preload,
    });
    if (!video.muxData) {
        return null;
    }
    const poster = getPoster({
        customThumbnailImage,
        customThumbnailTimestamp,
        customThumbnailType,
        startTime: startTime,
        useDefaultThumbnail,
        playbackId: video.muxData.playbackId,
    });
    const colorSet = createBasicColorSet(videoControlColors.backgroundColor, videoControlColors.textColor);
    const showBlurbAndMetadata = (blurb || metadata) && !showDisabledOverlay && !disabled;
    return ref ? (React.createElement(KeyboardDetectionProvider, null,
        React.createElement(VideoPlayerContainer, { id: `video-player-container-${video.muxData.playbackId}-${video.id}`, role: "button", aspectRatio: aspectRatio, loading: isLoading, showFullControls: showFullControls, disabled: disabled, disabledUntilHover: disabledUntilHover, backgroundImage: isLoading ? poster : undefined, showDisabledOverlay: showDisabledOverlay, showBlurbAndMetadata: showBlurbAndMetadata, onMouseEnter: () => {
                onMouseEnter();
            }, onMouseLeave: () => {
                onMouseLeave();
            }, onFocus: e => {
                if (!e.currentTarget.contains(e.relatedTarget))
                    onFocus();
            }, onBlur: e => {
                if (!e.currentTarget.contains(e.relatedTarget))
                    onBlur();
            }, onMouseDown: e => {
                e.preventDefault();
                if (!getIsControlButtonTarget(e.target)) {
                    onClick();
                }
            }, onKeyDown: e => {
                if (e.key === 'Enter' &&
                    !getIsControlButtonTarget(e.target)) {
                    onKeyboardEnter();
                }
            }, tabIndex: 0 },
            React.createElement("mux-video", { ref: ref, src: `https://stream.mux.com/${video.muxData.playbackId}.m3u8#t=0.0000000001`, "metadata-video-title": video.title, "alt-text": video.altText, style: { aspectRatio }, playsInline: true, preload: preload, poster: poster, "start-time": startTime, tabIndex: -1 }),
            React.createElement(ControlsAndOverlay, { tabIndex: -1 },
                React.createElement(React.Fragment, null, showPlayButtonOnly ? (React.createElement(CenteredPlayButton, { play: togglePlayButton, colorSet: colorSet })) : (React.createElement(FullControls, { vertical: aspectRatio === '9 / 16', includeSound: includeSound, colorSet: colorSet, isPlaying: isPlaying, isMuted: isMuted, togglePlay: togglePlayButton, toggleVolume: toggleVolume, disabled: disabled }))),
                (blurb || metadata) && (React.createElement(VideoOverlay, { blurb: blurb, metadata: metadata, className: "overlay" }))),
            React.createElement(LoadingOverlay, { tabIndex: -1, className: "loading-overlay" },
                React.createElement(LoadingIndicator, null))))) : null;
};
//# sourceMappingURL=Player.js.map